// DS2.3
import React from 'react';
import classnames from 'classnames';
import PlainButtonOrLink, { Props as BaseButtonProps } from '../PlainButtonOrLink';
import { Props as IconProps, IconSize } from '../../Icons/IconWrap';
import { ButtonSize } from '../types';
import useIconSizeForButtonSize from '../hooks/useIconSizeForButtonSize';
import s from './TextButton.scss';

export interface Props extends BaseButtonProps {
  size: ButtonSize;
  /** @deprecated in DSv2 it's already true */
  noHorizontalPaddings?: boolean;
  icon?: React.ComponentType<IconProps>;
  iconProps?: any;
  iconSizeOverride?: IconSize;
  postfix?: React.ComponentType<IconProps> | React.ReactNode;
  iconClassName?: string;
  variant: 'main' | 'approval-green' | 'destructive-red' | 'edit-purple' | 'core-orange';
  priority?: 'primary' | 'secondary';
  children?: React.ReactNode;
}

/**
 * If {@link componentOrNode} is a {@link React.ComponentType}, we'll render it via the supplied
 * {@link renderComponent}. Otherwise, we'll return it outright.
 *
 * Utility for props that accept a {@link React.ComponentType} or a {@link React.ReactNode}.
 *
 * ---
 * ```
 *  postfix: React.ComponentType<IconProps> | React.ReactNode;
 *
 *  // Then, in your component
 *  <>
 *    {renderComponentTypeOrNode(postfix, (PostfixIconComponent) =>
 *      // This is only called if `postfix` is a React.ComponentType
 *      <PostfixIconComponent {...yourProps} />
 *    )}
 *  </>
 * ```
 */
function renderComponentTypeOrNode<T>(
  componentOrNode: React.ComponentType<T> | React.ReactNode,
  renderComponent: (component: React.ComponentType<T>) => React.ReactNode,
  renderNode?: (node: React.ReactNode) => React.ReactNode
) {
  if (typeof componentOrNode === 'function') {
    return renderComponent(componentOrNode);
  }
  return renderNode ? renderNode(componentOrNode) : componentOrNode;
}

const TextButton: React.FC<Props> = ({
  size,
  noHorizontalPaddings = true,
  variant,
  priority = 'primary',
  className,
  children,
  icon: IconComponent,
  iconProps,
  iconSizeOverride,
  postfix,
  iconClassName,
  loading,
  ...props
}) => {
  const defaultIconSize = useIconSizeForButtonSize(size);
  const iconSize = iconSizeOverride ? iconSizeOverride : defaultIconSize;

  return (
    <PlainButtonOrLink
      className={classnames(
        s['root'],
        s[`variant-${variant}`],
        s[`priority-${priority}`],
        s[`size-${size}`],
        noHorizontalPaddings && s['no-horizontal-paddings'],
        props.disabled && s['disabled'],
        loading && s['loading'],
        className
      )}
      {...props}
    >
      {IconComponent && (
        <IconComponent
          className={classnames(s['prefix'], iconClassName)}
          size={iconSize}
          disableDefaultColors
          {...iconProps}
        />
      )}
      <span className={s['content']}>{children}</span>
      {postfix &&
        renderComponentTypeOrNode(
          postfix,
          PostfixIconComponent => (
            <PostfixIconComponent className={s['postfix']} size={iconSize} disableDefaultColors />
          ),
          node => <span className={s['postfix']}>{node}</span>
        )}
    </PlainButtonOrLink>
  );
};
TextButton.displayName = 'TextButton';

export default TextButton;
